import React, { useState, useEffect } from 'react';
import { Button, Input, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { DateRangePicker } from '../DateTime';
import { addDays, dayValue, isSameDay } from '../DateTime/calendarUtils';
import { formatDateBy, inclusiveEnd } from '../../utils/dates';

import './ConditionalPrice.scss';

const TIERS = {
    mid: { label: 'Mid', icon: 'fa-medal' },
    premium: { label: 'Premium', icon: 'fa-gem' },
};

// Days between consecutive periods that no period covers. A site has no price
// on those days, so it cannot order a screening then.
const findGaps = (bands) => {
    const dated = bands
        .filter(band => band.fromDate && band.toDate)
        .sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate));

    return dated.slice(1).flatMap((band, i) => {
        const from = addDays(dated[i].toDate, 1);
        const to = addDays(band.fromDate, -1);
        return dayValue(from) <= dayValue(to) ? [{ from, to }] : [];
    });
};

/**
 * The dated price periods of one price table, a tab per tier.
 * `emptyText` explains what a tier without periods means for the sites.
 */
const ConditionalPrice = ({ conditionalPrice = { mid: [], premium: [] }, handleChange, tiers, emptyText }) => {
    const [activeTab, setActiveTab] = useState(tiers?.includes('mid') ? 'mid' : 'premium');
    const [conditions, setConditions] = useState(conditionalPrice);
    const [dateRanges, setDateRanges] = useState({ mid: [], premium: [] });

    // Update active tab when tiers change
    useEffect(() => {
        if (!tiers?.includes(activeTab)) {
            setActiveTab(tiers?.includes('mid') ? 'mid' : 'premium');
        }
    }, [tiers, activeTab]);

    // Sync with parent conditionalPrice changes
    useEffect(() => {
        if (conditionalPrice) {
            setConditions(conditionalPrice);
        }
    }, [conditionalPrice]);

    const addCondition = (tier) => {
        const updatedConditions = {
            ...conditions,
            [tier]: [...(conditions[tier] || []), {}]
        };
        setConditions(updatedConditions);
    };

    const removeCondition = (tier, index) => {
        const updatedTierConditions = conditions[tier].filter((_, i) => i !== index);
        const updatedConditions = {
            ...conditions,
            [tier]: updatedTierConditions
        };
        setConditions(updatedConditions);

        const updatedDateRanges = {
            ...dateRanges,
            [tier]: dateRanges[tier].filter((_, i) => i !== index)
        };
        setDateRanges(updatedDateRanges);

        handleChange({ target: { name: 'conditionalPrice', value: updatedConditions } });
    };

    const handleDateChange = (tier, index, date) => {
        const isDateRange = Array.isArray(date) && date.length === 2;
        if (!isDateRange) return;

        const [start, end] = date;
        // The calendar returns the start of the end day. Stored as-is that day
        // is never priced and the next band cannot start until the day after,
        // which leaves a hole in the schedule — close the band at the end of
        // the day the admin picked.
        const closingEnd = inclusiveEnd(end);
        const tierConditions = [...(conditions[tier] || [])];
        const tierDateRanges = [...(dateRanges[tier] || [])];
        const newRange = { start, end: closingEnd };
        tierDateRanges[index] = newRange;

        const isOverlap = tierDateRanges.some((range, rangeIndex) => {
            if (rangeIndex === index || !range) return false;
            return !(range.end < start || range.start > closingEnd);
        });

        if (!isOverlap) {
            tierConditions[index] = { ...tierConditions[index], fromDate: start, toDate: closingEnd };
            const updatedConditions = { ...conditions, [tier]: tierConditions };
            const updatedDateRanges = { ...dateRanges, [tier]: tierDateRanges };

            setDateRanges(updatedDateRanges);
            setConditions(updatedConditions);
            handleChange({ target: { name: 'conditionalPrice', value: updatedConditions } });
        }
    };

    const handlePriceChange = (tier, index, event) => {
        const price = event.target.value;
        const tierConditions = conditions[tier].map((condition, i) => {
            if (index !== i) return condition;
            return { ...condition, price: price === '' ? '' : parseInt(price, 10) };
        });

        const updatedConditions = { ...conditions, [tier]: tierConditions };
        setConditions(updatedConditions);
        handleChange({ target: { name: 'conditionalPrice', value: updatedConditions } });
    };

    const excludeDates = (tier, index) => {
        return (conditions[tier] || [])
            .filter((_, i) => i !== index)
            .map(({ fromDate, toDate }) => ({ start: fromDate, end: toDate }));
    };

    const renderConditions = (tier) => {
        const tierConditions = conditions[tier] || [];
        const { label } = TIERS[tier];
        const gaps = findGaps(tierConditions);

        if (!tierConditions.length) {
            return (
                <div className="price-empty">
                    <span>{emptyText || 'No prices yet.'}</span>
                    <Button color="secondary" outline size="sm" onClick={() => addCondition(tier)}>
                        <span className="fas fa-plus me-2" aria-hidden="true" />
                        Add {label} period
                    </Button>
                </div>
            );
        }

        return (
            <>
                <div className="price-bands">
                    <div className="price-bands-header" aria-hidden="true">
                        <span>Dates</span>
                        <span className="text-end">Credits</span>
                        <span />
                    </div>
                    {tierConditions.map((condition, index) => (
                        <div className="price-band" key={index}>
                            <DateRangePicker
                                className="date-field"
                                size="sm"
                                value={{ from: condition.fromDate, to: condition.toDate }}
                                onChange={({ from, to }) => handleDateChange(tier, index, [from, to])}
                                excludeIntervals={excludeDates(tier, index)}
                                format="dd MMM yy"
                                placeholder="Select dates"
                            />
                            <Input
                                value={condition.price === '' ? '' : condition.price}
                                onChange={(e) => handlePriceChange(tier, index, e)}
                                type="number"
                                bsSize="sm"
                                min="0"
                                placeholder="0"
                                className="credits-input"
                                aria-label={`${label} credits, period ${index + 1}`}
                            />
                            <button
                                type="button"
                                className="band-remove"
                                onClick={() => removeCondition(tier, index)}
                                aria-label={`Remove ${label} period ${index + 1}`}
                                title="Remove period"
                            >
                                <span className="fas fa-xmark" aria-hidden="true" />
                            </button>
                        </div>
                    ))}
                </div>

                {gaps.map(({ from, to }) => (
                    <div className="price-gap" key={from.getTime()}>
                        <span className="fas fa-triangle-exclamation me-2" aria-hidden="true" />
                        {isSameDay(from, to)
                            ? `No price on ${formatDateBy(from, 'dd MMM yy')}`
                            : `No price from ${formatDateBy(from, 'dd MMM yy')} to ${formatDateBy(to, 'dd MMM yy')}`}
                    </div>
                ))}

                <Button color="link" size="sm" className="price-add" onClick={() => addCondition(tier)}>
                    <span className="fas fa-plus me-2" aria-hidden="true" />
                    Add period
                </Button>
            </>
        );
    };

    return (
        <div className="conditions">
            <Nav tabs className="mb-3">
                {['mid', 'premium'].filter(tier => tiers?.includes(tier)).map(tier => (
                    <NavItem key={tier}>
                        <NavLink
                            className={activeTab === tier ? 'active' : ''}
                            onClick={() => setActiveTab(tier)}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className={`fas ${TIERS[tier].icon} me-2`} />
                            {TIERS[tier].label}
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>

            <TabContent activeTab={activeTab}>
                <TabPane tabId="mid">
                    {renderConditions('mid')}
                </TabPane>
                <TabPane tabId="premium">
                    {renderConditions('premium')}
                </TabPane>
            </TabContent>
        </div>
    );
};

export default ConditionalPrice;
